/**
 * useLocale Hook
 *
 * Reads `settings.ui.language` from user preferences on login and syncs
 * the i18n provider locale accordingly. Also provides a `setLocale` method
 * that updates both the i18n provider and the backend preference.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  changeLanguage,
  getCurrentLanguage,
  SupportedLanguage,
  SUPPORTED_LANGUAGES,
} from './i18nCore';

/**
 * Platform-agnostic service interface for user preferences.
 * Consumers must supply their platform-specific implementation.
 */
export interface UserPreferencesAdapter {
  getPreferences: () => Promise<{
    settings: { ui?: { language?: string } };
  }>;
  updatePreferences: (partial: {
    ui: { language: string };
  }) => Promise<unknown>;
}

export interface UseLocaleReturn {
  /** Current locale code (e.g., 'en', 'uk') */
  locale: SupportedLanguage;
  /** Whether locale is being loaded from backend */
  isLoading: boolean;
  /** Set locale (updates i18n provider and backend) */
  setLocale: (locale: SupportedLanguage) => Promise<void>;
  /** Whether current locale differs from backend (sync in progress) */
  isSyncing: boolean;
}

/**
 * Hook that syncs the i18n locale with the user's stored preference.
 *
 * On mount (when `isAuthenticated` is true), reads `settings.ui.language`
 * from the user preferences API and updates the i18n provider.
 *
 * `setLocale` updates both the i18n provider and the backend.
 *
 * @param adapter - Platform-specific user preferences service
 * @param isAuthenticated - Whether the user is currently authenticated
 */
export function useLocale(
  adapter: UserPreferencesAdapter | null,
  isAuthenticated: boolean,
): UseLocaleReturn {
  const [locale, setLocaleState] = useState<SupportedLanguage>(getCurrentLanguage());
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const initializedRef = useRef(false);

  // On auth, read stored locale and apply it
  useEffect(() => {
    if (!isAuthenticated || !adapter || initializedRef.current) return;

    let cancelled = false;
    setIsLoading(true);

    adapter
      .getPreferences()
      .then((prefs) => {
        if (cancelled) return;
        const storedLang = prefs.settings?.ui?.language;
        if (storedLang && storedLang in SUPPORTED_LANGUAGES) {
          const lang = storedLang as SupportedLanguage;
          changeLanguage(lang);
          setLocaleState(lang);
        }
        initializedRef.current = true;
      })
      .catch(() => {
        // Silently fall back to current locale
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, adapter]);

  // Reset initialization flag on logout
  useEffect(() => {
    if (!isAuthenticated) {
      initializedRef.current = false;
    }
  }, [isAuthenticated]);

  const setLocale = useCallback(
    async (newLocale: SupportedLanguage) => {
      // Update i18n provider immediately for responsive UI
      await changeLanguage(newLocale);
      setLocaleState(newLocale);

      // Persist to backend if authenticated
      if (adapter && isAuthenticated) {
        setIsSyncing(true);
        try {
          await adapter.updatePreferences({ ui: { language: newLocale } });
        } catch (err) {
          console.error('Failed to persist locale preference:', err);
          // The i18n change is already applied; log error but don't revert
        } finally {
          setIsSyncing(false);
        }
      }
    },
    [adapter, isAuthenticated],
  );

  return {
    locale,
    isLoading,
    setLocale,
    isSyncing,
  };
}
